import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Drawer, Flex, MultiSelect, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getAvailableDealsForPersonFundLink,
	linkPersonToFund,
} from '@dealstack/frontend/shared/dataExchange/services/fund.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { UserRowWithActions } from '@dealstack/frontend/shared/components/UserRowWithActions/UserRowWithActions.tsx';

interface Props {
	personId: string;
	personName: string;
	fundId: string;
	fundName: string;
	hasPermission: boolean;
	opened: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

export const LinkPersonToFundDrawer = ({
	personId,
	personName,
	fundId,
	fundName,
	hasPermission,
	opened,
	onClose,
	onSuccess,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const [selectedDealIds, setSelectedDealIds] = useState<string[]>([]);
	const [lastAddedDealName, setLastAddedDealName] = useState<string | null>(null);

	const linkMutation = useMutation({
		mutationFn: (variables: Parameters<typeof linkPersonToFund>[0]) =>
			linkPersonToFund(variables),
	});

	const { data, isFetching } = useQuery({
		queryKey: ['availableDealsForPersonFundLink', { fundId, personId }],
		queryFn: ({ signal }) => getAvailableDealsForPersonFundLink({ fundId, personId }, signal),
		enabled: opened,
	});

	const availableDeals = useMemo(() => data?.deals ?? [], [data]);

	const dealOptions = useMemo(
		() =>
			availableDeals.map((deal) => ({
				label: deal.name,
				value: deal.id,
			})),
		[availableDeals]
	);

	const hasNoAvailableDeals = !dealOptions.length && !isFetching;

	useEffect(() => {
		if (!opened) {
			setSelectedDealIds([]);
			setLastAddedDealName(null);
		}
	}, [opened]);

	const handleDealSelectionChange = (value: string[]) => {
		const lastAddedId = value[value.length - 1];
		const name = dealOptions.find(({ value }) => value === lastAddedId)?.label ?? null;
		setLastAddedDealName(name);
		setTimeout(() => setLastAddedDealName(null), 3000);
		setSelectedDealIds(value);
	};

	const removeDealFromSelection = (dealId: string) => {
		setSelectedDealIds((prev) => prev.filter((id) => id !== dealId));
	};

	const selectedDeals = availableDeals.filter((deal) => selectedDealIds.includes(deal.id));

	const onSubmit = async () => {
		if (!selectedDealIds.length) {
			return;
		}

		try {
			await Promise.all(
				selectedDealIds.map((dealId) =>
					linkMutation.mutateAsync({ personId, dealId, fundId })
				)
			);

			showToastV2({
				title: tCapManage('fund_link.drawer.toast.success.title'),
				message: tCapManage('fund_link.drawer.toast.success.message'),
			});
			onClose();
			onSuccess?.();
		} catch {
			showToastV2({
				title: tCapManage('fund_link.drawer.toast.error.title'),
				message: tCapManage('fund_link.drawer.toast.error.message'),
				state: 'error',
			});
		}
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tCapManage('fund_person_link.drawer.title')}
		>
			<Flex direction="column" justify="space-between" h="100%">
				<Flex direction="column" gap="1rem">
					<MultiSelect
						data={dealOptions}
						value={selectedDealIds}
						onChange={handleDealSelectionChange}
						label={
							<Flex align="center" justify="space-between" gap="0.25rem">
								{tCapManage('fund_person_link.drawer.form.deal_label')}
								{lastAddedDealName && (
									<Flex align="center" gap="0.25rem" maw="60%">
										<Icon variant="holdings-checkmark" size="md" />
										<Flex align="center" gap="0.25rem">
											<Text fz="sm" fw={400} truncate lineClamp={1}>
												{lastAddedDealName}
											</Text>
											<Text
												fz="sm"
												fw={400}
												c="dark.0"
												style={{ whiteSpace: 'nowrap' }}
											>
												{tCapManage(
													'fund_person_link.drawer.form.added_to_list'
												)}
											</Text>
										</Flex>
									</Flex>
								)}
							</Flex>
						}
						labelProps={{
							style: { width: '100%' },
						}}
						placeholder={tCapManage('fund_person_link.drawer.form.deal_placeholder')}
						disabled={hasNoAvailableDeals || !hasPermission}
						rightSection={<Icon variant="caret-down" size="lg" />}
						styles={{ rightSection: { pointerEvents: 'none' } }}
						size="md"
						clearable
						multiple
						searchable
					/>

					{hasNoAvailableDeals && (
						<InfoBox
							iconVariant="information"
							alignContent="center"
							description={tCapManage('fund_person_link.drawer.no_deals_warning', {
								fundName,
							})}
						/>
					)}

					{!!selectedDeals.length && (
						<Flex direction="column">
							<Text fw={600}>
								{tCapManage('fund_person_link.drawer.selected_title')}
							</Text>
							<Text c="gray.9" mt="0.25rem" mb="0.5rem">
								{tCapManage('fund_person_link.drawer.selected_description', {
									personName,
									fundName,
								})}
							</Text>
							{selectedDeals.map((deal) => (
								<UserRowWithActions
									key={deal.id}
									displayName={deal.name}
									onUserDeleteClick={() => removeDealFromSelection(deal.id)}
									hasEditPermission={true}
									showInitialsAvatar={false}
								/>
							))}
						</Flex>
					)}
				</Flex>

				<Button
					onClick={onSubmit}
					disabled={linkMutation.isPending || !selectedDealIds.length || !hasPermission}
				>
					{tCapManage('fund_person_link.drawer.link_button')}
				</Button>
			</Flex>
		</Drawer>
	);
};
